import React, { useState, useEffect } from 'react'
import { useLazyQuery } from '@apollo/client'
import { useCart } from '../contexts/CartContext'
import { ShoppingCartIcon, TrashIcon, PlusIcon, MinusIcon, PencilSquareIcon, TagIcon, XMarkIcon, GiftIcon } from '@heroicons/react/24/outline'
import CheckoutModal from './CheckoutModal'
import { Order, OrderType } from '../types/order'
import { useDiscountPreview } from '../hooks/useDiscountPreview'
import { CartTransformationService } from '../lib/cartTransformation'
import { useOrderCharges } from '../hooks/useOrderCharges'
import { LOOKUP_COUNTER_CODE } from '../graphql/loyalty-counter-queries'
import { CounterCodeLookup } from '../types/loyalty'

// Curry Me Club counter-code alphabet: no 0/O/1/I, upper-case, 6 chars.
const CLUB_CODE_ALPHABET = '23456789ABCDEFGHJKMNPQRSTVWXYZ'
const CLUB_CODE_LENGTH = 6
const sanitizeClubCode = (raw: string): string =>
  raw
    .toUpperCase()
    .split('')
    .filter((ch) => CLUB_CODE_ALPHABET.includes(ch))
    .join('')
    .slice(0, CLUB_CODE_LENGTH)

interface CartDisplayProps {
  onEditItem: (itemId: string) => void
}

const CartDisplay: React.FC<CartDisplayProps> = ({ onEditItem }) => {
  const { cart, removeItem, updateItemQuantity, clearCart, setOrderType } = useCart()
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [couponInput, setCouponInput] = useState('')
  const [appliedCode, setAppliedCode] = useState('')

  // Curry Me Club — the counter code (§4/§5). `appliedClubCode` is only set once
  // lookupCounterCode found a member; it feeds the preview (so the reward prices
  // into the total) and the create mutation (so the member/reward attach).
  const [clubInput, setClubInput] = useState('')
  const [appliedClubCode, setAppliedClubCode] = useState('')
  const [clubMember, setClubMember] = useState<CounterCodeLookup | null>(null)
  const [clubError, setClubError] = useState<string | null>(null)

  const { preview, loading: previewLoading, couponError } = useDiscountPreview(
    appliedCode,
    appliedClubCode || undefined
  )

  const [lookupCounterCode, { loading: clubLookupLoading }] = useLazyQuery<{
    lookupCounterCode: CounterCodeLookup | null
  }>(LOOKUP_COUNTER_CODE, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const found = data?.lookupCounterCode ?? null
      if (!found) {
        // §5: null is not an error — the code is unknown, expired or used.
        setClubError('Code unknown or expired')
        setClubMember(null)
        setAppliedClubCode('')
        return
      }
      setClubError(null)
      setClubMember(found)
      setAppliedClubCode(sanitizeClubCode(clubInput))
    },
    onError: () => {
      // A real network/permission failure (already toasted by errorLink).
      setClubError('Code unknown or expired')
      setClubMember(null)
      setAppliedClubCode('')
    },
  })

  const applyClubCode = () => {
    const code = sanitizeClubCode(clubInput)
    if (code.length !== CLUB_CODE_LENGTH) return
    setClubError(null)
    lookupCounterCode({ variables: { code } })
  }

  const removeClubCode = () => {
    setClubInput('')
    setAppliedClubCode('')
    setClubMember(null)
    setClubError(null)
  }

  // Clear any applied coupon or club code once the cart empties — after an order
  // completes or the cart is cleared — so nothing carries into the next customer.
  useEffect(() => {
    if (cart.items.length === 0) {
      setAppliedCode('')
      setCouponInput('')
      setClubInput('')
      setAppliedClubCode('')
      setClubMember(null)
      setClubError(null)
    }
  }, [cart.items.length])

  const formatPrice = (priceCents: number) => {
    return `€${(priceCents / 100).toFixed(2)}`
  }

  // Statiegeld sits outside the goods total: no BTW, never discounted. Both
  // cart.total_cents and the discount preview are goods-only, so the deposit is
  // added once here to produce what the customer actually pays.
  const depositTotalCents = CartTransformationService.calculateCartDepositCents(cart)
  // Verpakkingskosten follows the order type the cashier picked — 0 for dine-in.
  // Asked per order type: switching the toggle re-asks the API rather than
  // applying a local copy of the rule about who pays.
  const { packagingFeeCents, packagingFeeUnknown, packagingFeeLoading } = useOrderCharges(cart.order_type)
  const goodsPayableCents = preview && preview.discount_amount_cents > 0
    ? preview.discounted_total_cents
    : cart.total_cents
  const payableTotalCents = goodsPayableCents + depositTotalCents + packagingFeeCents
  const checkoutBlockedReason = appliedCode && couponError
    ? 'Fix the invalid coupon code before proceeding'
    : packagingFeeUnknown
      ? 'Verpakkingskosten could not be read from the server — reload before charging this order'
      : undefined
  // Debug logging removed

  const getItemTotal = (item: any) => {
    const basePrice = item.price_cents * item.quantity
    const optionsPrice = item.selected_options.reduce((sum: number, option: any) => 
      sum + (option.price_delta_cents * item.quantity), 0)
    return basePrice + optionsPrice
  }

  const Empty = () => (
    <div className="flex-1 flex items-center justify-center p-6">
      <div className="text-center">
        <ShoppingCartIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Your cart is empty</h3>
        <p className="mt-1 text-sm text-gray-500">Start adding items to your cart</p>
      </div>
    </div>
  )

  // Group bundle parts under their main (by bundle_parent_id)
  const mains = cart.items.filter(it => it.type === 'main')
  const byParent: Record<string, any[]> = {}
  cart.items.forEach(it => {
    if (it.type !== 'main' && it.bundle_parent_id) {
      if (!byParent[it.bundle_parent_id]) byParent[it.bundle_parent_id] = []
      byParent[it.bundle_parent_id].push(it)
    }
  })

  const getChildTotal = (child: any) => {
    const base = (child.price_cents || 0) * (child.quantity || 0)
    const opts = (child.selected_options || []).reduce((sum: number, o: any) => sum + (o.price_delta_cents || 0) * (child.quantity || 0), 0)
    return base + opts
  }

  const getMainWithChildrenTotal = (main: any) => {
    const children = byParent[main.id] || []
    const childrenTotal = children.reduce((sum: number, c: any) => sum + getChildTotal(c), 0)
    return getItemTotal(main) + childrenTotal
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">
            Cart ({cart.item_count} {cart.item_count === 1 ? 'item' : 'items'})
          </h3>
          <button onClick={clearCart} className="text-sm text-red-600 hover:text-red-800">Clear all</button>
        </div>
        {/* Order type toggle */}
        <div className="flex rounded-lg border border-gray-300 overflow-hidden text-sm font-medium">
          {(['DINE_IN', 'TAKEAWAY'] as OrderType[]).map((type) => (
            <button
              key={type}
              onClick={() => setOrderType(type)}
              className={`flex-1 py-1.5 transition-colors ${
                cart.order_type === type
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              {type === 'DINE_IN' ? 'Dine In' : 'Takeaway'}
            </button>
          ))}
        </div>
      </div>

      {/* Items (fills available space, scrolls only when needed) */}
      <div className="flex-1 overflow-y-auto">
        {mains.length === 0 ? (
          <Empty />
        ) : (
          <div className="divide-y divide-gray-200">
            {mains.map((main) => (
              <div key={main.id} className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center">
                      <h4 className="font-semibold text-gray-900 truncate">{main.product_name}</h4>
                      <span className="ml-2 px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">main</span>
                    </div>
                    <div className="mt-3 pl-3 border-l-2 border-gray-200">
                      {(() => {
                        const children = byParent[main.id] || []
                        const bundle = children.find((c: any) => c.type === 'bundle')
                        const parts = children.filter((c: any) => c.type !== 'bundle')
                        return (
                          <ul className="space-y-1">
                            {bundle && (
                              <li className="text-sm">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center min-w-0">
                                    <span className="text-gray-900">
                                      {bundle.relationship_type === 'BUNDLE_UPGRADE' ? 'Meal Deal (upgrade)' : 'Bundle Upgrade'}
                                    </span>
                                  </div>
                                  <div className="text-xs font-medium text-gray-900">
                                    <span className={bundle.price_cents > 0 ? 'text-red-600' : 'text-green-600'}>
                                      {bundle.price_cents > 0 ? '+' : ''}€{(bundle.price_cents / 100).toFixed(2)}
                                    </span>
                                  </div>
                                </div>
                              </li>
                            )}
                            <li className="text-sm">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center min-w-0">
                                  <span className="text-gray-700 truncate">Main: {main.product_name} — {main.product_variant_name}</span>
                                </div>
                                <div className="text-xs font-medium text-gray-900">
                                  {formatPrice(getItemTotal(main))}
                                </div>
                              </div>
                              {main.selected_options.length > 0 && (
                                <ul className="ml-6 mt-1 space-y-0.5">
                                  {main.selected_options.map((opt: any, idx: number) => (
                                    <li key={idx} className="text-xs text-gray-600 flex items-center justify-between">
                                      <span className="truncate">{opt.option_group_name ? `${opt.option_group_name.toUpperCase()}: ` : '• '}{opt.option_item_label}</span>
                                      {opt.price_delta_cents !== 0 && (
                                        <span className={opt.price_delta_cents > 0 ? 'text-red-600' : 'text-green-600'}>
                                          {opt.price_delta_cents > 0 ? '+' : ''}€{(opt.price_delta_cents / 100).toFixed(2)}
                                        </span>
                                      )}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </li>
                            {parts.map((child: any) => (
                              <li key={child.id} className="text-sm">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center min-w-0">
                                    <span className="text-gray-700 truncate">
                                      {child.relationship_type === 'INCLUDED_PART' ? '+' : ''}
                                      {child.bundle_group_name ? `${child.bundle_group_name}: ` : ''}
                                      {child.product_name}{child.product_variant_name ? ` — ${child.product_variant_name}` : ''}
                                      {child.relationship_type === 'INCLUDED_PART' ? ' (included)' : ''}
                                    </span>
                                  </div>
                                  <div className="text-xs font-medium text-gray-900">
                                    {formatPrice((child.price_cents || 0) * (child.quantity || 1))}
                                  </div>
                                </div>
                                {child.selected_options && child.selected_options.length > 0 && (
                                  <ul className="ml-6 mt-1 space-y-0.5">
                                    {child.selected_options.map((opt: any, idx: number) => (
                                      <li key={idx} className="text-xs text-gray-600 flex items-center justify-between">
                                        <span className="truncate">{opt.option_group_name ? `${opt.option_group_name.toUpperCase()}: ` : '• '}{opt.option_item_label}</span>
                                        {opt.price_delta_cents !== 0 && (
                                          <span className={opt.price_delta_cents > 0 ? 'text-red-600' : 'text-green-600'}>
                                            {opt.price_delta_cents > 0 ? '+' : ''}€{(opt.price_delta_cents / 100).toFixed(2)}
                                          </span>
                                        )}
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </li>
                            ))}
                          </ul>
                        )
                      })()}
                    </div>
                  </div>

                  <div className="ml-4 text-right">
                    <div className="text-lg font-semibold text-gray-900">{formatPrice(getMainWithChildrenTotal(main))}</div>
                    <div className="flex items-center space-x-2 mt-2">
                      <button onClick={() => updateItemQuantity(main.id, main.quantity - 1)} className="p-1 text-gray-400 hover:text-gray-600">
                        <MinusIcon className="h-4 w-4" />
                      </button>
                      <span className="text-sm font-medium text-gray-900 w-8 text-center">{main.quantity}</span>
                      <button onClick={() => updateItemQuantity(main.id, main.quantity + 1)} className="p-1 text-gray-400 hover:text-gray-600">
                        <PlusIcon className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="flex items-center space-x-1 mt-2">
                      <button onClick={() => onEditItem(main.id)} className="p-1 text-blue-400 hover:text-blue-600" title="Edit item">
                        <PencilSquareIcon className="h-4 w-4" />
                      </button>
                      <button onClick={() => removeItem(main.id)} className="p-1 text-red-400 hover:text-red-600" title="Remove item">
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer pinned to bottom */}
      <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 space-y-3">
        {/* Coupon code section */}
        {mains.length > 0 && (
          <div>
            {appliedCode ? (
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center space-x-1.5 text-green-700">
                  <TagIcon className="h-4 w-4" />
                  <span className="font-medium">{appliedCode}</span>
                  {previewLoading && <span className="text-gray-400">…</span>}
                </div>
                <button
                  onClick={() => { setAppliedCode(''); setCouponInput('') }}
                  className="text-gray-400 hover:text-gray-600"
                  title="Remove coupon"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={couponInput}
                  onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                  onKeyDown={(e) => { if (e.key === 'Enter' && couponInput.trim()) setAppliedCode(couponInput.trim()) }}
                  placeholder="Coupon code"
                  className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={() => { if (couponInput.trim()) setAppliedCode(couponInput.trim()) }}
                  disabled={!couponInput.trim()}
                  className="px-3 py-1.5 text-sm font-medium bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Apply
                </button>
              </div>
            )}
            {couponError && (
              <p className="mt-1 text-xs text-red-600">{couponError}</p>
            )}
          </div>
        )}

        {/* Curry Me Club — counter code */}
        {mains.length > 0 && (
          <div>
            {clubMember ? (
              <div className="rounded-md border border-gray-200 bg-white p-3 space-y-1.5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 text-gray-900">
                    <GiftIcon className="h-4 w-4 text-primary-600" />
                    <span className="font-semibold">{clubMember.member.first_name}</span>
                    {clubMember.member.card && (
                      <span className="text-sm text-gray-500">
                        · card {clubMember.member.card.position} of {clubMember.member.card.size}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={removeClubCode}
                    className="text-gray-400 hover:text-gray-600"
                    title="Remove club code"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
                {clubMember.reward ? (
                  <p className="text-sm text-gray-700">
                    Reward on this order: <span className="font-medium">{clubMember.reward.name_nl}</span>
                    {preview && preview.loyalty_reward_cents > 0 && (
                      <span className="text-green-700"> (−{formatPrice(preview.loyalty_reward_cents)})</span>
                    )}
                  </p>
                ) : (
                  <p className="text-sm text-gray-500">Identify only — a stamp, no reward on this order.</p>
                )}
                {/* §6: the stamp lands on payment, never at checkout. */}
                <p className="text-xs text-gray-400">The stamp is added when the payment completes.</p>
                {/* §7: the reward could not be priced (e.g. below minimum, staff
                    meal). The order still goes through without it. */}
                {preview?.loyalty_message && (
                  <p className="text-xs text-amber-700">{preview.loyalty_message}</p>
                )}
              </div>
            ) : (
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={clubInput}
                  onChange={(e) => { setClubInput(sanitizeClubCode(e.target.value)); setClubError(null) }}
                  onKeyDown={(e) => { if (e.key === 'Enter') applyClubCode() }}
                  placeholder="Club code"
                  maxLength={CLUB_CODE_LENGTH}
                  className="flex-1 px-3 py-1.5 text-sm tracking-widest uppercase border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <button
                  onClick={applyClubCode}
                  disabled={clubInput.length !== CLUB_CODE_LENGTH || clubLookupLoading}
                  className="px-3 py-1.5 text-sm font-medium bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {clubLookupLoading ? '…' : 'Club code'}
                </button>
              </div>
            )}
            {clubError && !clubMember && (
              <p className="mt-1 text-xs text-red-600">{clubError}</p>
            )}
          </div>
        )}

        {/* Totals */}
        {preview && preview.discount_amount_cents > 0 ? (
          <div className="space-y-1">
            <div className="flex items-center justify-between text-sm text-gray-600">
              <span>Subtotal</span>
              <span>{formatPrice(cart.total_cents)}</span>
            </div>
            {preview.product_discount_cents > 0 && (
              <div className="flex items-center justify-between text-sm text-green-700">
                <span>{preview.product_discount_name ?? 'Product discount'}</span>
                <span>-{formatPrice(preview.product_discount_cents)}</span>
              </div>
            )}
            {preview.order_discount_cents > 0 && (
              <div className="flex items-center justify-between text-sm text-green-700">
                <span>{preview.order_discount_name ?? 'Order discount'}</span>
                <span>-{formatPrice(preview.order_discount_cents)}</span>
              </div>
            )}
            {preview.loyalty_reward_cents > 0 && (
              <div className="flex items-center justify-between text-sm text-green-700">
                <span>{preview.loyalty_reward_name_nl ?? 'Club reward'}</span>
                <span>-{formatPrice(preview.loyalty_reward_cents)}</span>
              </div>
            )}
            {depositTotalCents > 0 && (
              <div className="flex items-center justify-between text-sm text-gray-600">
                <span>Statiegeld</span>
                <span>{formatPrice(depositTotalCents)}</span>
              </div>
            )}
            {packagingFeeCents > 0 && (
              <div className="flex items-center justify-between text-sm text-gray-600">
                <span>Verpakkingskosten</span>
                <span>{formatPrice(packagingFeeCents)}</span>
              </div>
            )}
            <div className="flex items-center justify-between pt-1 border-t border-gray-200">
              <span className="text-lg font-semibold text-gray-900">Total</span>
              <span className="text-xl font-bold text-green-700">{formatPrice(payableTotalCents)}</span>
            </div>
          </div>
        ) : depositTotalCents > 0 || packagingFeeCents > 0 ? (
          <div className="space-y-1">
            <div className="flex items-center justify-between text-sm text-gray-600">
              <span>Subtotal</span>
              <span>{formatPrice(cart.total_cents)}</span>
            </div>
            {depositTotalCents > 0 && (
              <div className="flex items-center justify-between text-sm text-gray-600">
                <span>Statiegeld</span>
                <span>{formatPrice(depositTotalCents)}</span>
              </div>
            )}
            {packagingFeeCents > 0 && (
              <div className="flex items-center justify-between text-sm text-gray-600">
                <span>Verpakkingskosten</span>
                <span>{formatPrice(packagingFeeCents)}</span>
              </div>
            )}
            <div className="flex items-center justify-between pt-1 border-t border-gray-200">
              <span className="text-lg font-semibold text-gray-900">Total</span>
              <span className="text-xl font-bold text-gray-900">{formatPrice(payableTotalCents)}</span>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <span className="text-lg font-semibold text-gray-900">Total</span>
            <span className="text-xl font-bold text-gray-900">{formatPrice(cart.total_cents)}</span>
          </div>
        )}

        {/* The fee is unknown, not zero. Saying so is the whole point: the API
            adds it regardless and rejects a total without it, so a silent €0,00
            here becomes an unexplained refusal after the customer has paid. */}
        {packagingFeeUnknown && !packagingFeeLoading && (
          <div className="rounded-md bg-amber-50 border border-amber-300 px-3 py-2 text-sm text-amber-800">
            Verpakkingskosten unavailable — check the connection and reload before charging this order.
          </div>
        )}
        <button
          onClick={() => setIsCheckoutOpen(true)}
          disabled={cart.items.length === 0 || (!!appliedCode && !!couponError) || packagingFeeUnknown}
          title={checkoutBlockedReason}
          className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Proceed to Checkout
        </button>
      </div>

      {/* Checkout Modal */}
      <CheckoutModal
        isOpen={isCheckoutOpen}
        couponCode={couponError ? undefined : appliedCode || undefined}
        counterCode={appliedClubCode || undefined}
        discountedTotalCents={preview?.discounted_total_cents}
        onClose={() => {
          setIsCheckoutOpen(false)
          setError(null)
        }}
        onOrderCreated={(_order: Order) => {
          setIsCheckoutOpen(false)
          setError(null)
        }}
        onError={(errorMessage: string) => {
          setError(errorMessage)
        }}
      />

      {/* Error Display */}
      {error && (
        <div className="fixed bottom-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}
    </div>
  )
}

export default CartDisplay
